import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2, AlertTriangle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PasswordField, isPasswordValid } from "@/components/PasswordField";
import { useSeo } from "@/hooks/useSeo";

export default function ResetPassword() {
  useSeo("Neues Passwort setzen – LAIzyDocu", "Vergeben Sie ein neues Passwort für Ihr LAIzyDocu-Konto.");
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { if (!token) toast.error("Kein gültiger Token im Link."); }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!isPasswordValid(password)) { toast.error("Das Passwort erfüllt nicht alle Anforderungen."); return; }
    if (password !== confirm) { toast.error("Die Passwörter stimmen nicht überein."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Zurücksetzen fehlgeschlagen."); }
    finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-stone-50 grid place-items-center px-6">
        <div data-testid="reset-invalid" className="w-full max-w-md bg-white rounded-3xl border border-stone-100 p-8 text-center">
          <div className="w-16 h-16 rounded-2xl bg-orange-50 text-orange-600 grid place-items-center mx-auto mb-6"><AlertTriangle className="w-8 h-8" /></div>
          <h1 className="font-display text-2xl font-semibold text-emerald-900">Ungültiger Link</h1>
          <Link to="/passwort-vergessen" className="inline-block mt-6"><Button className="h-12 rounded-xl bg-emerald-900 text-white">Neuen Link anfordern</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 grid place-items-center px-6 py-10">
      <div className="w-full max-w-md bg-white rounded-3xl border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 md:p-10">
        <Link to="/login" className="inline-flex items-center gap-2 mb-6">
          <div className="w-10 h-10 rounded-xl bg-emerald-900 text-white grid place-items-center"><ShieldCheck className="w-5 h-5" /></div>
          <span className="font-display text-lg font-semibold text-emerald-900">LA<span className="text-emerald-600">I</span>zyDoc</span>
        </Link>

        {done ? (
          <div data-testid="reset-done" className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-emerald-50 text-emerald-700 grid place-items-center mx-auto mb-6"><CheckCircle2 className="w-8 h-8" /></div>
            <h1 className="font-display text-2xl font-semibold text-emerald-900">Passwort aktualisiert</h1>
            <p className="text-stone-600 mt-3">Sie können sich jetzt mit Ihrem neuen Passwort anmelden.</p>
            <Button onClick={() => navigate("/login", { replace: true })} data-testid="reset-to-login"
              className="w-full h-14 mt-7 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white">Zur Anmeldung</Button>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="reset-form">
            <h1 className="font-display text-3xl font-semibold text-emerald-900">Neues Passwort setzen</h1>
            <p className="text-stone-500 mt-2 mb-6">Wählen Sie ein sicheres neues Passwort.</p>
            <div className="space-y-5">
              <PasswordField value={password} onChange={setPassword} label="Neues Passwort" testid="reset-password" />
              <div>
                <Label htmlFor="reset-confirm" className="text-base font-medium text-stone-800">Passwort wiederholen</Label>
                <Input id="reset-confirm" data-testid="reset-confirm" type="password" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" className="h-14 px-4 text-lg rounded-xl mt-2" />
                {confirm && confirm !== password && <p className="text-xs text-orange-600 mt-1">Passwörter stimmen nicht überein.</p>}
              </div>
              <Button type="submit" data-testid="reset-submit" disabled={loading}
                className="w-full h-16 text-lg rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white">
                {loading ? "Wird gespeichert…" : "Passwort speichern"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
